/** @jsx jsx */
import React, { useState } from 'react';

import SelectForm from 'components/molecules/SelectForm';
import FormSubmitArea from 'components/atoms/Form/FormSubmitArea';
import FormLabel from 'components/atoms/Form/FormLabel';
import FlexBox from 'components/atoms/FlexBox';
import productColor, {
  grayScale, textFontSize,
} from 'components/styles';
import DataTable from 'components/organismos/DataTable/DataTable';
import { css, jsx } from '@emotion/core';
import TransferHolidayStatusDomain from 'domain/master/holidayManagement/transferHolidayStatus';
import { Column } from 'react-table';
import Modal from 'components/molecules/Modal';
import moment from 'moment';

import IconLabelButton from 'components/molecules/IconLabelButton';
import CalendarForm from 'components/molecules/CalendarForm';
import VerticalLabelTextLayout from 'components/atoms/Form/VerticalLabelTextLayout';
import { isSafari, isIPad13, isTablet } from 'react-device-detect';
import { useHolidayManagementRestForm, useDownloadHolidayManagementRest } from './hooks';
import BlockUI from 'components/molecules/BlockUi';
import FormPadding from 'components/atoms/Form/FormPadding';
import Calendar from 'components/molecules/CalendarV1';
import TextTooltip from 'components/molecules/TextTooltip';

export interface DataDetail {
  staffCode: string,
  staffName: string,
  transferDayNumb: string
}

// create style form
const styles = {
  form: css({
    // paddingTop: '15px',
  }),
  wrapper: css({
    display: 'flex',
    width: '150px',
    float: 'left',
  }),
  input: css({
    flex: 1,
    width: '100px',
    padding: '7px',
    fontSize: textFontSize.re,
    fontFamily: 'inherit',
    color: productColor.primary,
    borderTop: `1px solid ${grayScale.gray10}`,
    borderRight: 'none',
    borderBottom: `1px solid ${grayScale.gray10}`,
    borderLeft: `1px solid ${grayScale.gray10}`,
    borderRadius: '4px 0 0 4px',
  }),
  addon: css({
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    width: '100px',
    padding: '7px',
    color: productColor.primary,
    backgroundColor: productColor.primaryM95,
    borderTop: `1px solid ${grayScale.gray10}`,
    borderRight: `1px solid ${grayScale.gray10}`,
    borderBottom: `1px solid ${grayScale.gray10}`,
    borderLeft: 'none',
    borderRadius: '0 4px 4px 0',
  }),
  container: css({
    padding: '4px 0',
  }),
  label: css({
    flexBasis: '100px',
  }),
  selectDay: css({
    minWidth: '100px',
    width: '170px',
  }),
  detail: css({
    backgroundColor: 'white',
    borderRadius: '5px',
    textAlign: 'center',
    marginLeft: '5px',
  }),
  wrapAccordion: css({
    backgroundColor: '#fff',
    border: '1px solid #dcdcdc',
  }),
  table: css({
    color: '#666666'
  }, css`  
    table.table-bordered tr td,
    table.table-bordered tr th{
      border:1px solid #dcdcdc;
      font-size:14px;
    } 
  `),
  detailButton: css({
    padding: '4px',
    color: productColor.primary,
    fontSize: '12px'
  }),
  globalSearch: css({
    marginLeft: '30px',
    marginBottom: '10px'
  }),
  customStyle: css({
    display: 'flex',
    justifyContent: ((isSafari && isTablet) || isIPad13) ? 'flex-start' : 'flex-end',
    paddingRight: ((isSafari && isTablet) || isIPad13) ? '10px' : '30px',
    marginBottom: ((isSafari && isTablet) || isIPad13) ? '0px' : '-55px'
  }, css`
    button{
      width:auto;
      margin-left:15px
    }
    button:hover{
      background:transparent
    }
    button svg{
      width:20px !important;
      height:20px !important;
    }
  `),
  note: css({
    paddingLeft: '5px',
    fontSize: '14px',
    color: '#666666',
    position: 'relative'
  })
};

const HolidayManagementRestForm: React.FC<{ orgTreesOptions: any }> = ({
  orgTreesOptions,
}) => {
  const {
    transferHolidayStatusList,
    callGetTransferHolidayStatusDetail,
    orgCode,
    setOrgCode,
    searchDate,
    setSearchDate,
    modalOpen,
    setModalOpen,
    closeModal,
    detailItems,
  } = useHolidayManagementRestForm();

  const {
    downloadHolidayManagementRest, blocking
  } = useDownloadHolidayManagementRest(
    orgCode,
    searchDate,
  );

  //
  const [dataDetail, setDataDetail] = useState<DataDetail>({
    staffCode: '',
    staffName: '',
    transferDayNumb: '',
  });

  // handlePaidHolidayStatusDetail
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleTransferHolidayStatusDetail = async (data: any) => {
    setDataDetail(data);
    callGetTransferHolidayStatusDetail(data.staffCode);
    setModalOpen(true);
  };

  //
  const columns: Array<Column<TransferHolidayStatusDomain>> = React.useMemo(() => [
    {
      Header: () => 'スタッフコード',
      id: 'staffCode',
      accessor: 'staffCode',
      Cell: (cell: {
        row: { isExpanded: boolean, original: TransferHolidayStatusDomain },
        data: Array<TransferHolidayStatusDomain>
      }) => (
          <FlexBox>
            <TextTooltip 
              text={cell.row.original.staffCode}
              textDis={(
                <span>{cell.row.original.staffCode}</span>
              )}
              customCss={css({
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                maxWidth: '220px',
                whiteSpace: 'nowrap',
                display: 'inline-block',
                margin: '0px auto',
              })}
              />
            {/* <div css={css(styles.detail)}>
              <ClickableIconButton
                onClick={() => handleTransferHolidayStatusDetail(cell.row.original)}
                additionalCss={styles.detailButton}
              >
                詳細
            </ClickableIconButton>
            </div> */}

            <IconLabelButton
              onClick={() => handleTransferHolidayStatusDetail(cell.row.original)}
              text="詳細"
              isIcon={false}
            />
          </FlexBox>
        ),
    },
    {
      Header: 'スタッフ名',
      accessor: 'staffName',
    },
    {
      Header: '振替日数',
      accessor: 'transferDayNumbStr',
      Cell: (cell: { row: { isExpanded: boolean, original: any } }) => (
        <div style={{ textAlign: 'center' }}>
          {cell.row.original.transferDayNumbStr}
        </div>
      ),
    },
  ], [handleTransferHolidayStatusDetail]);

  return (
    <BlockUI blocking={blocking}>
      <div css={styles.wrapAccordion}>
        <div css={css(styles.form)}>
          {/* <FormContentFilter> */}
          <FormPadding customStyle={css({ paddingTop:'16px',paddingLeft:'10px', paddingRight:'10px', maxWidth:'580px'})}>
             <FlexBox>
                <div css={styles.label}>
                  <FormLabel
                    label="対象期間"
                  />
                </div>
                <div css={styles.selectDay}>
                  {/* <CalendarForm required={true} label="" date={new Date(searchDate)} dateFormat='YYYY年MM月DD日' changeDate={(date: Date) => setSearchDate(date)} /> */}
                  <Calendar 
                    date={new Date(searchDate)}
                    setDate={(date) => {
                      if(date){
                        setSearchDate(date)
                      }
                    }}
                  />
                </div>
                <div css={styles.note}>から過去1年間</div>
              </FlexBox>
          </FormPadding>
          <FormPadding customStyle={css({paddingLeft:'10px', paddingRight:'10px', maxWidth:'580px'})}>
            <SelectForm
              label="組織"
              name="orgCode004"
              value={String(orgCode)}
              setValue={(val: string) => setOrgCode(val)}
              options={orgTreesOptions}
            />
          </FormPadding>   
          {/* </FormContentFilter> */}
          <div
            css={css`
              table th:nth-of-type(1) {
                width: 200px;
              }
              table th:nth-of-type(2) {
                width: 200px;
              }
              table th:nth-of-type(3) {
                width: 100px;
              }
            `}
          >
            <FormSubmitArea customStyle={styles.customStyle}>
              <IconLabelButton onClick={() => downloadHolidayManagementRest()} iconType="download" text="ダウンロード" />
              {/* <IconLabelButton onClick={() => downloadHolidayManagementRest(true)} iconType="download" text="全組織ダウンロード" /> */}
            </FormSubmitArea>
            <div className="table-bordered" style={{ overflowX: 'auto', whiteSpace: 'nowrap', marginTop: '10px', padding:'0 10px 10px'}}>
              <DataTable
                classNameParrant="border-left-right"
                className="table-no-border-left-right" 
                columns={columns}
                data={transferHolidayStatusList}
                isGlobalFilter={true}
                // globalSearchStyle={styles.globalSearch}
              />
            </div>
          </div>

          {/* Form Detail */}
          <Modal
            open={modalOpen}
            closeHandler={closeModal}
            title="取得状況詳細"
            submitText=""
            onSubmit={closeModal}
            closeText="閉じる"
          >
            <div style={{ width: '350px', margin: '0 auto', fontSize: '14px' }} css={styles.table}>
              <VerticalLabelTextLayout
                label={`スタッフコード`}
                variable={dataDetail.staffCode}
              />
              <VerticalLabelTextLayout
                label={`スタッフ名`}
                variable={dataDetail.staffName}
              />
              <VerticalLabelTextLayout
                label={`期間`}
                variable={`${moment(detailItems.targetDateFrom).format('YYYY年MM月DD日')}～${moment(detailItems.targetDateTo).format('YYYY年MM月DD日')}`}
              />
              <table className="table-bordered" style={{ width: '100%', fontSize: '14px' }}>
                <thead>
                  <tr>
                    <th>振替出勤日</th>
                    <th>振替休日</th>
                  </tr>
                </thead>
                <tbody>
                  {detailItems.attendTransfer && detailItems.attendTransfer.map((item: any, index: number) => (
                    <tr key={index} style={{textAlign: 'center'}}>
                      <td>
                        {moment(item.targetDate).format('YYYY年MM月DD日')}
                      </td>
                      <td>
                        {moment(item.transferHolidayDate).format('YYYY年MM月DD日')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Modal>
        </div>
      </div>
    </BlockUI>
  );
};
export default HolidayManagementRestForm;
